import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { ISourcedCard } from '../../../Models/CardModels';
import { IVisibleGameState, IVisiblePlay } from '../../../Models/StateModels';
import Chat from '../Chat/Chat';
import { isReaction } from '../Dock/Dock';
import GameHeader from '../GameHeader/GameHeader';
import GameOver from '../GameOver/GameOver';
import GameRules from '../GameRules/GameRules';
import GameState from '../GameState/GameState';
import { IChatMessage, IVisibleLobby } from '../../../Models/LobbyModels';

interface IGameScreenProps {
  visibleGameState: IVisibleGameState;
  playerId: number;
  playHistory: IVisiblePlay[];
  tryPlay: (play: ISourcedCard[]) => Promise<void>;
  leaveGame: () => Promise<void>;
  trySwap: (card1: ISourcedCard, card2: ISourcedCard) => Promise<void>;
  tryPickup: () => void;
  handleError: (e: Error) => void;

  sendMessage: (message: string) => void;

  lobby: IVisibleLobby | undefined;
  messages: IChatMessage[];
}

const SHEET_TRANSITION = { duration: 0.25, ease: 'easeOut' } as const;

type Sheet = 'chat' | 'rules';

export default function GameScreen(props: IGameScreenProps) {
  const { visibleGameState, lobby, messages, leaveGame, sendMessage } = props;
  const [openSheet, setOpenSheet] = useState<Sheet>();
  const [lastSeenMessage, setLastSeenMessage] = useState<
    IChatMessage | undefined
  >(messages[messages.length - 1]);
  const playerIdHash = visibleGameState.player.idHash;

  const unseenMessages = messages.slice(
    lastSeenMessage ? messages.indexOf(lastSeenMessage) + 1 : 0
  );
  const hasUnreadMessages = unseenMessages.some(
    (m) => m.playerIdHash !== playerIdHash && !isReaction(m.message)
  );

  const toggleSheet = (sheet: Sheet) => {
    if (openSheet === 'chat' || sheet === 'chat') {
      setLastSeenMessage(messages[messages.length - 1]);
    }
    setOpenSheet(openSheet === sheet ? undefined : sheet);
  };

  const sendChatMessage = (message: string) => {
    sendMessage(message);
    if (message.toLowerCase() === '/leave') {
      leaveGame();
    }
  };

  return (
    <article className="flex h-full flex-col select-none [&_input]:select-text">
      <GameHeader
        isChatOpen={openSheet === 'chat'}
        isRulesOpen={openSheet === 'rules'}
        hasUnreadMessages={hasUnreadMessages}
        toggleChat={() => toggleSheet('chat')}
        toggleRules={() => toggleSheet('rules')}
        leaveGame={leaveGame}
      />
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <motion.article
          layoutScroll
          inert={!!openSheet}
          className="h-full overflow-auto bg-page"
        >
          <div className="relative h-full">
            <GameState {...props} />
          </div>
        </motion.article>
        <AnimatePresence>
          {openSheet && (
            <motion.div
              key={openSheet}
              className="absolute inset-0 z-20"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={SHEET_TRANSITION}
            >
              {openSheet === 'chat' ? (
                <Chat
                  players={lobby?.players ?? []}
                  messages={messages}
                  playerIdHash={playerIdHash}
                  sendMessage={sendChatMessage}
                />
              ) : (
                <GameRules options={visibleGameState.ruleSetOptions} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {visibleGameState.gameOver && (
        <GameOver
          visibleGameState={visibleGameState}
          playHistory={props.playHistory}
          leaveGame={leaveGame}
        />
      )}
    </article>
  );
}
